// ============================================================
// ドメイン別 backend 切り替え / 機能フラグ(公開 API 層)
//
// 店舗依存値の実体は instances.ts(SSOT)に集約済み。本ファイルは既存の呼び出し
// 元との互換のため、instances.ts を参照する薄いラッパー群を提供する。
//
// booking.trailernchu.co.jp と pit-booking.trailernchu.co.jp は
// シンボリックリンクで同一ビルド(同一 JS バンドル)を共有しているため、
// ビルド時 env では backend を分けられない。実行時に hostname / REACT_APP_INSTANCE で
// 判定して backend API ベースURL・機能フラグを出し分ける。
// ============================================================

import {
  getInstance,
  getInstanceConfig,
  storeKeyFromSquareIndex,
  INSTANCES,
  type AppInstance,
} from './instances';

export { getInstance };
export type { AppInstance };

// 宮古 backend の絶対URL。ホスト名判定は dev の REACT_APP_INSTANCE を見ないため、
// getInstanceConfig ではなく instances.ts の miyako 値を直接使う。
const MIYAKO_BACKEND = INSTANCES.miyako.backendApi as string;

/**
 * 現在のホスト名に対応する backend API ベースURLの上書きを返す。
 * 上書き対象でなければ null(= 既定の .env 値を使う)。
 *
 * 注意: localhost:3003 の宮古 dev は setupProxy.js の /dispatch-api プロキシで
 *   miyako に向ける方式(CORS 回避)のため、REACT_APP_INSTANCE では上書きしない。
 *   ブラウザ実行時のホスト名(pit-booking.*)でのみ絶対URLに上書きする。
 */
export function getBackendApiOverride(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const host = window.location.hostname;
  if (host === 'pit-booking.trailernchu.co.jp' || host.startsWith('pit-booking.')) {
    return MIYAKO_BACKEND;
  }
  return null;
}

/**
 * 顧客向けリンク(/sys/form/・/sys/checkin/ 等)のベースになる公開サイトのオリジン。
 * 那覇 = https://n-car.jp / 宮古 = https://pit-rent.com。
 *
 * /sys/ 配下は両ドメインから同一実体へのシンボリックリンクで配信されており、
 * sys/public/store_site.php の site_store_config() が HTTP_HOST で店舗を判定して
 * ブランディング・backend 接続先を出し分ける。したがってフロントが渡すオリジンを
 * 出し分ければ、宮古のリンクは正しく宮古として表示・動作する。
 *
 * ⚠️ constants.ts の SITE_ORIGIN(那覇固定)とは別物。あちらは Square 請求書 API
 *   (SYS_INVOICE_BASE)と公開 calc API のベースで、どちらも現状 那覇専用のため
 *   宮古に切り替えてはいけない。顧客向けリンクにはこちらを使うこと。
 */
export function getSiteOrigin(): string {
  return getInstanceConfig().siteOrigin;
}

/**
 * このインスタンスで LINE 機能(メニュー・ページ・チャット)を有効にするか。
 * 宮古(miyako)は当面 LINE を使わないため無効。
 */
export function isLineEnabled(): boolean {
  return getInstanceConfig().features.line;
}

/** クルーズ船（/cruise）を出すか。両店舗 true（那覇は 2026-10-31〜） */
export function isCruisePricingEnabled(): boolean {
  return getInstanceConfig().features.cruisePricing;
}

/**
 * このインスタンスの LINE API(sys/line/api_v2.php)のベース URL。
 * LINE 無効店舗(宮古)は `''` を返す。
 *
 * ⚠️ LINE API は**那覇にしか存在しない**。api_v2.php の各アクションは
 *   那覇 DB の line_users / line_messages / agent_requests をそのまま読むだけで、
 *   店舗の次元を一切持たない。したがって宮古のブラウザが叩くと
 *   「那覇の顧客データが宮古の画面に出る」「宮古の操作が那覇のログ/Pusher を汚す」
 *   という二重の事故になる(2026-07-30 に daylist の実況・LINEインジケータで実際に発生)。
 *
 * そのため戻り値が `''` のときは**通信してはいけない**。呼び出し側は URL を
 * 組み立てる前に空判定するか、空なら reject するラッパー経由で fetch すること。
 */
export function getLineApiUrl(): string {
  const c = getInstanceConfig();
  if (!c.features.line || c.lineApiUrl === null) return '';
  return process.env.REACT_APP_LINE_API_URL || c.lineApiUrl;
}

/**
 * このインスタンスで自社チャット（予約者チャット）を LINE ページに並べるか。
 * 那覇・宮古とも true（booking-platform/md/customer-chat.md Phase 2）。
 */
export function isReservationChatEnabled(): boolean {
  const c = getInstanceConfig();
  return c.features.reservationChat && c.chatApiUrl !== null;
}

/**
 * このインスタンスの自社チャット API（new-n-car `frontend/public/api/chat.php`）の URL。
 * 無効店舗は `''`（LINE API と同じく、空なら通信しない）。
 *
 * 那覇 = n-car.jp / 宮古 = pit-rent.com で **DB も別**。宮古のブラウザが那覇の URL を叩くと
 * 那覇の予約者の会話が宮古の画面に出るので、URL は必ずここから取る。
 */
export function getReservationChatApiUrl(): string {
  const c = getInstanceConfig();
  if (!c.features.reservationChat || c.chatApiUrl === null) return '';
  return process.env.REACT_APP_CHAT_API_URL || c.chatApiUrl;
}

/**
 * 自社チャット（LP）の Pusher 接続情報（公開 key）。無効店舗・未設定は key が '' でリアルタイム無し（ポーリングのみ）。
 * 購読認証は chat.php?action=pusher_auth（Bearer）。設計: md/master_md/page-line-chat.md「リアルタイム」
 */
export function getReservationChatPusherConfig(): { key: string; cluster: string } {
  const c = getInstanceConfig();
  if (!isReservationChatEnabled() || !c.chatPusherKey || !c.chatPusherCluster) return { key: '', cluster: '' };
  return { key: c.chatPusherKey, cluster: c.chatPusherCluster };
}

/** LP の店舗キー（Pusher payload の `store` と比較。那覇 r-cal の instance 'default' は LP では 'naha'） */
export function getReservationChatStoreKey(): 'naha' | 'miyako' {
  return getInstanceConfig().chatStoreKey;
}

/**
 * /line ページ（チャット・ボード）とそのメニューを出すか。
 * LINE 連携か自社チャットのどちらかが有効なら出す。宮古は LINE 無効・自社チャット有効なので
 * 宮古でも /line が開き、自社チャットだけが並ぶ（LINE 固有の UI は isLineEnabled() で個別に隠す）。
 */
export function isChatBoardEnabled(): boolean {
  return isLineEnabled() || isReservationChatEnabled();
}

/**
 * このインスタンスの Pusher 接続情報(app key / cluster)を解決して返す。
 *
 * booking と pit-booking は**同一バンドルを共有**するためビルド時 env では
 * 店舗別にできない。app key は公開値なので instances.ts に店舗別で持たせ、
 * null のときだけ .env にフォールバックする(= 那覇。既存挙動を維持)。
 *
 * key が空('')の店舗は「Pusher アプリ未作成」を意味し、接続せずポーリングを継続する。
 */
export function getPusherConfig(): { key: string; cluster: string } {
  const c = getInstanceConfig();
  const key = c.pusherKey !== null ? c.pusherKey : (process.env.REACT_APP_PUSHER_APP_KEY || '');
  const cluster = c.pusherCluster !== null ? c.pusherCluster : (process.env.REACT_APP_PUSHER_CLUSTER || '');
  return { key, cluster };
}

/**
 * このインスタンスで Pusher(リアルタイム同期)を有効にするか。
 *
 * ⚠️ 店舗ごとに**別の Pusher アプリ**を使う(同一アカウント内の別アプリ)。
 *    backend の DispatchEvent::broadcastOn() が `dispatch-updates` を
 *    ハードコードしている(別会社リポジトリ・両 backend 同一コード)ため、
 *    同一アプリを共用すると両店舗が同じチャネルに publish して予約が混線する。
 *
 * features.pusher が true でも、その店舗の app key が未設定なら false を返す。
 * これにより「アプリを作る前に有効化してしまう」事故を防ぐ。
 * 無効の間は REST の forceSync + 30秒ポーリングで最新化する
 * (ポーリングの ON/OFF は本関数の戻り値から導出される。useGlobalReservationSync 参照)。
 */
export function isPusherEnabled(): boolean {
  if (!getInstanceConfig().features.pusher) return false;
  const { key, cluster } = getPusherConfig();
  return key !== '' && cluster !== '';
}

/**
 * このインスタンスで Square 請求書機能(発行・取り消し)を有効にするか。
 * 2026-07-27 に宮古も有効化(PIT の認証は .env.invoice に設定済み)。
 */
export function isSquareEnabled(): boolean {
  return getInstanceConfig().features.square;
}

/**
 * このログイン名が、この店舗の「共用アカウント」かどうか。
 *
 * 共用アカウント = カウンターで複数人が使う1つのログイン。誰が今対応しているかを
 * 「対応スタッフ」ピッカーで選び分ける。個人アカウントならログイン者自身が対応者になる。
 *
 * ⚠️ 2026-08-03 まで各所で `user.username === 'staff'` と直書きしていた。
 *   'staff' は**那覇にしか無い login_id** のため、宮古では担当者ボタンが
 *   完全に不活性になり、メニュー内の「本日勤務」すら開けなかった。
 *   共用アカウントの有無は店舗ごとに違うので instances.ts を正とする。
 */
export function isSharedAccount(username: string | null | undefined): boolean {
  const shared = getInstanceConfig().sharedAccountLogin;
  if (!shared) return false;
  return String(username || '') === shared;
}

/**
 * このインスタンスで実況ウィンドウ(LiveWindow)を有効にするか。
 *
 * ⚠️ isLineEnabled() で代用しないこと。2026-07-30 は「実況＝LINE 依存」として
 *   LINE フラグで塞いだが、2026-08-01 に agent_requests を店舗別化したことで
 *   宮古でも自店の保留中リクエストから実況を生成できるようになった。
 *   LINE 受信(材料B)が無いぶん催促検知だけが効かない構成になる。
 */
export function isLiveReportEnabled(): boolean {
  return getInstanceConfig().features.liveReport;
}

/**
 * このインスタンスの店舗キー。'ncar'(那覇) | 'pit'(宮古)。
 *
 * ⚠️ 店舗を表す語彙は本システムに**2系統**ある。混同しないこと。
 *   A) 店舗キー   'ncar' / 'pit'      … PHP 側の唯一の語彙
 *      (sys/ota/stores.php・sys/public/store_site.php・sys/config/master_config.php・
 *       sys/line/pusher_helper.php の第1引数・agent_requests.store)
 *   B) インスタンス名 'default' / 'miyako' … フロントの getInstance() と digest_logs.instance
 *
 * B は「書き手がフロントだけ」のテーブル(digest_logs)向けに生まれたもので、
 * サーバ側と値をやり取りする新規実装では **A を使う**こと。
 * 3つ目の語彙を作らないため、変換はこの関数1箇所に閉じる。
 */
export function getStoreKey(): 'ncar' | 'pit' {
  return storeKeyFromSquareIndex(getInstanceConfig().squareIndex);
}

/**
 * このインスタンスの Square 設定番号(sqNum)。那覇=0 / 宮古=2。
 * 請求書作成 API へ必ず明示的に送ること。省略すると server 側が 0(那覇)を既定に
 * するため、宮古の請求書が那覇の Square に作られる。
 */
export function getSquareIndex(): number {
  return getInstanceConfig().squareIndex;
}

/**
 * この invoice_link を現在のインスタンスから操作してよいか判定する。
 *
 * 宮古の予約には移行データ由来で 那覇 Square の請求書(sq:0:...)が残っており、
 * 宮古 UI から取り消すと那覇の実請求書を操作してしまう。sqNum から店舗を逆引きし、
 * 現在の店舗と一致するときだけ許可する(server の store_resolver.php と同一定義)。
 *
 * @param sqNumFromLink invoice_link から解決した sqNum(prefix 無しの旧データは 0 = 那覇)
 */
export function canOperateSquareIndex(sqNumFromLink: number): boolean {
  return (
    storeKeyFromSquareIndex(sqNumFromLink) ===
    storeKeyFromSquareIndex(getInstanceConfig().squareIndex)
  );
}
